import { useMemo } from 'react';
import { useIntegrations } from '@/queries/useIntegrations';
import type { IntegrationData } from '@/types';
import type { InstagramIntegrationData, InstagramStatus } from '../types/automation.types';

const INSTAGRAM_INTEGRATION_TYPE = 'instagram';

/**
 * Hook to check Instagram integration status and get username
 * Vue implementation: vue/src/pages/automations/Automations.vue (computed properties)
 */
export const useInstagramIntegration = () => {
    const { data: integrations, isLoading, refetchIntegrations } = useIntegrations();

    const instagramIntegration = useMemo(() => {
        if (!integrations) return null;
        // IntegrationsResponse is Record<string, IntegrationData>
        const integrationsList = Object.values(integrations);
        return integrationsList.find((integration: IntegrationData) => integration.type === INSTAGRAM_INTEGRATION_TYPE);
    }, [integrations]);

    const instagramStatus: InstagramStatus = useMemo(() => {
        if (isLoading) return 'loading';
        if (!instagramIntegration) return 'not-connected';
        // Status 1 means connected and active
        return instagramIntegration.status === 1 ? 'connected' : 'not-connected';
    }, [isLoading, instagramIntegration]);

    const integrationData = instagramIntegration?.data as InstagramIntegrationData | undefined;
    const igUsername = integrationData?.ig_username;
    const isCommentSpamFlagged = integrationData?.comment_spam_flag ?? false;

    return {
        instagramStatus,
        igUsername,
        isCommentSpamFlagged,
        isLoading,
        refetchIntegrations,
    };
};
